import { z } from 'zod'
import { ianaTimeZone, isoDate } from './common.js'
import { itemKind } from './items.js'

/**
 * Reading a booking confirmation into the fields of an item.
 *
 * Ana books a hotel, gets a mail from the property, and then retypes six things
 * out of it into this product. That retyping is the largest single chunk of
 * per-booking work she does, and it is also where a transposed booking
 * reference gets introduced — because the failure is silent until somebody is
 * standing at a desk.
 *
 * Two things about this contract are deliberate and load-bearing.
 *
 * **Nothing here is an instant.** A confirmation says "departs 07:05". That is a
 * wall clock, and a wall clock is not a moment until a zone is named — see
 * PATTERNS §3b, which exists because reading a Paris arrival on São Paulo's
 * clock produced a plausible wrong answer rather than an error. So the parse
 * returns the date and the clock reading as they were printed, plus its best
 * guess at the zone they are read in, and the advisor's side converts through
 * `wallClockToInstant` exactly as the item form does. There is still only one
 * converter.
 *
 * **This is a draft, not a save.** The response describes what the model read;
 * nothing reaches the database until Ana confirms it through the ordinary
 * create-an-item path. A confirmation number she never looked at is worse than
 * one she retyped.
 */

/**
 * The most text one paste may carry.
 *
 * A confirmation mail with the hotel's newsletter footer attached runs long, so
 * this is generous; it exists to bound what gets sent to a paid API by the
 * length of a mis-paste, not to police formatting.
 */
export const MAX_CONFIRMATION_LENGTH = 20_000

/**
 * How many bookings one paste may yield.
 *
 * A round-trip flight is two, a multi-city ticket can be six. Twenty is past
 * anything real and stops one bad paste from rendering a page of drafts.
 */
export const MAX_PARSED_ITEMS = 60

/**
 * What the pasted text is. A confirmation is one supplier's mail with printed
 * facts; notes are Ana's own shorthand about a whole trip, with days and
 * places and no booking references to speak of. Same draft shape out, a
 * different brief in — and the same rule that nothing is saved unseen.
 */
export const parseMode = z.enum(['confirmation', 'notes'])
export type ParseMode = z.infer<typeof parseMode>

export const parseConfirmationRequest = z.object({
  /** The email or the notes, pasted. Plain text; nothing here renders it as HTML. */
  text: z.string().trim().min(1).max(MAX_CONFIRMATION_LENGTH),
  mode: parseMode.default('confirmation'),
})
export type ParseConfirmationRequest = z.infer<typeof parseConfirmationRequest>

/** A clock reading as printed on a booking — `HH:MM`, and nothing about where. */
export const wallClockTime = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM')

/**
 * One booking, as read. Every field is nullable because a confirmation mail
 * carries whatever the issuing system felt like carrying, and a missing field
 * is an ordinary outcome rather than a failure.
 */
export const parsedItem = z.object({
  kind: itemKind,
  title: z.string().min(1).max(200),
  /** The date printed on the booking, in the calendar of the place it happens. */
  startDate: isoDate.nullable(),
  startTime: wallClockTime.nullable(),
  endDate: isoDate.nullable(),
  endTime: wallClockTime.nullable(),
  /**
   * Where the clock above is read, when the mail says enough to know. Null
   * means "wherever the trip is", which is true of nearly everything that is
   * not a flight.
   */
  timezone: ianaTimeZone.nullable(),
  /** Where it ends, when that is somewhere else. An arriving flight, mostly. */
  endTimezone: ianaTimeZone.nullable(),
  address: z.string().max(500).nullable(),
  phone: z.string().max(40).nullable(),
  website: z.string().max(2000).nullable(),
  /**
   * The number read out at a desk.
   *
   * The server refuses to hand back one that does not literally appear in the
   * pasted text — see `confirmations.service.ts`. Of every field here this is
   * the one where a single wrong character is silently useless, and it is the
   * one field a mail always states verbatim, so the check is both cheap and
   * exactly aimed.
   */
  confirmationRef: z.string().max(200).nullable(),
  notes: z.string().max(2_000).nullable(),
})
export type ParsedItem = z.infer<typeof parsedItem>

/**
 * What came back.
 *
 * Three outcomes rather than an error code, because only one of them is an
 * error. `unavailable` means this server was started without a key and the
 * feature simply is not there — the same posture as a missing MapTiler key, and
 * not something to render as "something went wrong". `failed` is the provider
 * having a bad morning. `read` with an empty list is a real answer: the paste
 * was not a confirmation.
 */
export const parseConfirmationResponse = z.discriminatedUnion('status', [
  z.object({ status: z.literal('read'), items: z.array(parsedItem).max(MAX_PARSED_ITEMS) }),
  z.object({ status: z.literal('unavailable') }),
  z.object({ status: z.literal('failed') }),
])
export type ParseConfirmationResponse = z.infer<typeof parseConfirmationResponse>
